import {
  DELETE_ROUTINE_DRAFT,
  FIND_ROUTINE_DRAFT,
  isDraftEnabledForRoutineType,
  useCustomMutation,
  useCustomQuery,
  useWorkspaceAsset,
  useWorkspaceRoutine
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  RoutineDraftModal,
  RoutineForm,
  RoutineModules,
  SlideAction,
  useSlideLayoutSafe
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { RefObject, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutineSubmitSource } from '../assetUtils';

type AssetRoutineSubmitColumnProps = {
  assetId: string;
  backGuardRef?: RefObject<((onProceed: () => void, onCancel?: () => void) => void) | null>;
};

export const AssetRoutineSubmitColumn = ({ assetId, backGuardRef }: AssetRoutineSubmitColumnProps) => {
  const slideLayoutContext = useSlideLayoutSafe();
  const { routineId } = useParams<{ routineId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { workspaceAsset, isLoading: isAssetLoading } = useWorkspaceAsset(assetId);
  const routine = useWorkspaceRoutine(routineId ?? '');

  const [showDraftModal, setShowDraftModal] = useState(false);
  const [loadedDraftData, setLoadedDraftData] = useState<any>(null);
  const [formKey, setFormKey] = useState(0);
  const [justSavedDraft, setJustSavedDraft] = useState(false);
  const from: RoutineSubmitSource = location.state?.from ?? RoutineSubmitSource.AssetCard;
  const cameFromRoutinesList = from === RoutineSubmitSource.RoutinesList;
  const returnUrl = cameFromRoutinesList ? `/assets/${assetId}/routines/all` : `/assets/${assetId}`;

  useEffect(() => {
    setShowDraftModal(false);
    setLoadedDraftData(null);
    setJustSavedDraft(false);
  }, [routineId]);

  const isRoutineChanging = !!routine && routine.id !== routineId;
  const showSaveDraftButton = !isRoutineChanging && isDraftEnabledForRoutineType(routine?.type);

  const draftCacheKey = ['routineDraft', workspaceAsset?.id, routineId];

  const { data: draftQueryData, isLoading: isDraftLoading } = useCustomQuery({
    cacheKey: draftCacheKey,
    query: FIND_ROUTINE_DRAFT,
    variables: {
      workspaceAssetId: workspaceAsset?.id || '',
      routineId: routineId || '',
    },
    options: {
      enabled: !!(workspaceAsset?.id && routineId && showSaveDraftButton),
    },
  });

  const { mutateAsync: deleteDraftAsync } = useCustomMutation({
    query: DELETE_ROUTINE_DRAFT,
  });

  const draft = draftQueryData?.routine?.findRoutineDraft;

  useEffect(() => {
    if (!isDraftLoading && routine && showSaveDraftButton) {
      if (draft && !justSavedDraft) {
        setShowDraftModal(true);
      } else {
        setLoadedDraftData(null);
      }
    }
  }, [draft, isDraftLoading, justSavedDraft, routine, showSaveDraftButton]);

  useEffect(() => {
    if (slideLayoutContext) {
      if (isAssetLoading || isDraftLoading || isRoutineChanging) {
        slideLayoutContext.setColumnTitle('loading');
      } else {
        slideLayoutContext.setColumnTitle(routine?.name || 'routines.editRoutines.editRoutinesButton');
      }
    }
  }, [isAssetLoading, isDraftLoading, isRoutineChanging, routine]);


  if (isAssetLoading || isDraftLoading || isRoutineChanging || !workspaceAsset || !routine) {
    return <LoadingIndicator />;
  }

  const module = routine.type ? RoutineModules[routine.type] : undefined;

  if (!module) {
    return null;
  }

  const onCompleted = async () => {
    try {
      if (workspaceAsset?.id && routineId) {
        await deleteDraftAsync({
          input: { workspaceAssetId: workspaceAsset.id, routineId },
        });
        await queryClient.invalidateQueries({ queryKey: draftCacheKey });
      }
    } catch (error) {
      console.error('Error deleting draft after completion:', error);
    }
    
    if (slideLayoutContext) {
      await slideLayoutContext.onSlideChange(SlideAction.PREV);
    }

    navigate(returnUrl);
  };

  const handleSaveDraft = async () => {
    setJustSavedDraft(true);
    await queryClient.invalidateQueries({ queryKey: draftCacheKey });
    
    if (slideLayoutContext) {
      await slideLayoutContext.onSlideChange(SlideAction.PREV);
    }

    navigate(returnUrl);
  };

  const handleContinueDraft = () => {
    setShowDraftModal(false);
    setLoadedDraftData(draft?.data || null);
    setFormKey((prev) => prev + 1);
  };

  const handleStartNew = async () => {
    setShowDraftModal(false);
    try {
      if (workspaceAsset?.id && routineId) {
        await deleteDraftAsync({
          input: { workspaceAssetId: workspaceAsset.id, routineId },
        });
        await queryClient.invalidateQueries({ queryKey: draftCacheKey });
      }
      setLoadedDraftData(null);
      setFormKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  const handleDraftCancel = async () => {
    setShowDraftModal(false);
    
    if (slideLayoutContext) {
      await slideLayoutContext.onSlideChange(SlideAction.PREV);
    }

    navigate(returnUrl);
  };

  return (
    <View style={styles.container}>
      <RoutineForm
        key={formKey}
        workspaceAsset={workspaceAsset}
        module={module}
        routine={routine}
        onCompleted={onCompleted}
        topBar={() => <></>}
        onRegisterBackGuard={backGuardRef ? (guard) => { backGuardRef.current = guard; } : undefined}
        showSaveDraftButton={showSaveDraftButton}
        onSaveDraft={handleSaveDraft}
        draftData={loadedDraftData}
      />
      {showDraftModal && (
        <RoutineDraftModal
          visible={showDraftModal}
          onClose={handleDraftCancel}
          onContinueDraft={handleContinueDraft}
          onStartNew={handleStartNew}
          draft={draft}
          workspaceAsset={workspaceAsset}
          routine={routine}
          showPortal={false}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  }
});
